import { css } from '@emotion/core';
import { useState } from 'react';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import AuthenticatedRoute from './components/AuthenticatedRoute';
import AppMetadataContextWrapper from './components/AppMetadataContextWrapper';
import LoadingScreenContext from './services/LoadingScreenContext';
import AuthenticatedRouteLanguageContext from './services/AuthenticatedRouteLanguageContext';
import UnauthenticatedRouteLanguageContext from './services/UnauthenticatedRouteLanguageContext';
import useForceReloadApp from './services/useForceReloadApp';
import useAuthenticatedRouteLanguage from './services/useAuthenticatedRouteLanguage';
import useUnauthenticatedRouteLanguage from './services/useUnauthenticatedRouteLanguage';
import ChangeUsernamePage from './views/change-username-page/ChangeUsernamePage';
import LoginPageContainer from './views/LoginPage';
import LearnMorePage from './views/LearnMorePage';
import ChangePasswordPage from './views/change-password-page';
import ErrorPage from './views/error-page';
import ForgotPasswordPage from './views/forgot-password-page';
import Home from './views/home/Home';
import Studies from './views/studies/Studies';
import ChangePassword from './views/my-account/change-password';
import Emails from './views/my-account/emails';
import General from './views/my-account/general';
import RegistrationSuccess from './views/my-account/registration-success/RegistrationSuccess';
import RegisterPage from './views/register-page';
import { AuthenticatedPaths, UnauthenticatedPaths } from './constants/Paths';
import mobileContainerHeightCSS from './constants/mobileContainerHeightCSS';
import SelfRegistrationPage from './views/self-registration-page';
import VaultsPage from './views/VaultsPage';
import RecentActivity from './views/my-account/recent-activity/RecentActivity';

export default function App() {
    const [loading, setLoading] = useState(false);
    const [authenticatedRouteLanguage, setAuthenticatedRouteLanguage] =
        useAuthenticatedRouteLanguage();
    const [unauthenticatedRouteLanguage, setUnauthenticatedRouteLanguage] =
        useUnauthenticatedRouteLanguage();
    const key = useForceReloadApp();

    return (
        <div
            css={css`
                display: flex;
                flex-direction: column;
                ${mobileContainerHeightCSS};
            `}
        >
            <LoadingScreenContext.Provider
                // Key is used only for reloading the app at the root level; it has nothing to do with loading.
                key={key}
                value={{
                    loading,
                    setLoading,
                }}
            >
                <AppMetadataContextWrapper>
                    <UnauthenticatedRouteLanguageContext.Provider
                        value={{
                            messageLanguage: unauthenticatedRouteLanguage,
                            setMessageLanguage: setUnauthenticatedRouteLanguage,
                        }}
                    >
                        <AuthenticatedRouteLanguageContext.Provider
                            value={{
                                messageLanguage: authenticatedRouteLanguage,
                                setMessageLanguage: setAuthenticatedRouteLanguage,
                            }}
                        >
                            <Router>
                                <Switch>
                                    <Route path={UnauthenticatedPaths.ERROR}>
                                        <ErrorPage />
                                    </Route>
                                    <AuthenticatedRoute path={AuthenticatedPaths.HOME} />
                                    <AuthenticatedRoute path={AuthenticatedPaths.SYSTEMS}>
                                        <Home />
                                    </AuthenticatedRoute>
                                    <AuthenticatedRoute path={AuthenticatedPaths.STUDIES}>
                                        <Studies />
                                    </AuthenticatedRoute>
                                    <AuthenticatedRoute path={AuthenticatedPaths.MY_ACCOUNT}>
                                        <Switch>
                                            <Route path={AuthenticatedPaths.EMAILS}>
                                                <Emails />
                                            </Route>
                                            <Route path={AuthenticatedPaths.CHANGE_PASSWORD}>
                                                <ChangePassword />
                                            </Route>
                                            <Route path={AuthenticatedPaths.REGISTRATION_SUCCESS}>
                                                <RegistrationSuccess />
                                            </Route>
                                            <Route path={AuthenticatedPaths.RECENT_ACTIVITY}>
                                                <RecentActivity />
                                            </Route>
                                            <Route exact path={AuthenticatedPaths.MY_ACCOUNT}>
                                                <General />
                                            </Route>
                                            <Route
                                                path={`${AuthenticatedPaths.MY_ACCOUNT}:allOthers`}
                                            >
                                                <ErrorPage />
                                            </Route>
                                        </Switch>
                                    </AuthenticatedRoute>
                                    <AuthenticatedRoute path={AuthenticatedPaths.VAULT_INVITES}>
                                        <VaultsPage />
                                    </AuthenticatedRoute>
                                    <Route path={UnauthenticatedPaths.CHANGE_USERNAME}>
                                        <ChangeUsernamePage />
                                    </Route>
                                    <Route path={UnauthenticatedPaths.FORGOT_PASSWORD}>
                                        <ForgotPasswordPage />
                                    </Route>
                                    <Route path={UnauthenticatedPaths.CHANGE_PASSWORD}>
                                        <ChangePasswordPage />
                                    </Route>
                                    <Route path={UnauthenticatedPaths.REGISTER}>
                                        <RegisterPage />
                                    </Route>
                                    <Route path={UnauthenticatedPaths.SELF_REGISTER}>
                                        <SelfRegistrationPage />
                                    </Route>
                                    <Route path={UnauthenticatedPaths.LEARN_MORE}>
                                        <LearnMorePage />
                                    </Route>
                                    <Route path={UnauthenticatedPaths.LOGIN}>
                                        <LoginPageContainer />
                                    </Route>
                                </Switch>
                            </Router>
                        </AuthenticatedRouteLanguageContext.Provider>
                    </UnauthenticatedRouteLanguageContext.Provider>
                </AppMetadataContextWrapper>
            </LoadingScreenContext.Provider>
        </div>
    );
}
